import React from 'react';
import { AnimatedLoader } from './AnimatedLoader';

interface FullScreenLoaderProps {
  message?: string;
  showOverlay?: boolean;
  backgroundColor?: string;
  foregroundColor?: string;
  mutedForegroundColor?: string;
  accentColor?: string;
  logoUrl?: string;
  logoAlt?: string;
}

/**
 * Full-screen loading component with animated loader and optional message
 * Provides a consistent loading experience across the application
 */
export const FullScreenLoader = ({
  message = 'Loading...',
  showOverlay = true,
  backgroundColor,
  foregroundColor,
  mutedForegroundColor,
  accentColor,
  logoUrl,
  logoAlt = 'Company Logo',
}: FullScreenLoaderProps) => {
  const overlayClasses = showOverlay
    ? 'fixed inset-0 bg-gray-900 bg-opacity-100 z-50'
    : 'min-h-screen bg-gray-900';
  const isBranded = Boolean(backgroundColor || foregroundColor || accentColor || logoUrl);

  return (
    <div
      className={`${overlayClasses} flex flex-col items-center justify-center px-6`}
      role="status"
      aria-live="polite"
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {logoUrl && (
        <img
          src={logoUrl}
          alt={logoAlt}
          className="mb-10 h-14 w-auto max-w-full object-contain"
          style={{ maxWidth: 'min(72vw, 30rem)' }}
          loading="eager"
          decoding="sync"
        />
      )}
      {isBranded && accentColor ? (
        <div className="relative flex h-20 w-20 items-center justify-center" aria-hidden="true">
          <div
            className="absolute inset-0 rounded-full border-4"
            style={{ borderColor: accentColor, opacity: 0.22 }}
          />
          <div
            className="absolute inset-0 animate-spin rounded-full border-4 border-transparent"
            style={{ borderRightColor: accentColor, borderTopColor: accentColor }}
          />
          <div className="h-3 w-3 rounded-full shadow-lg" style={{ backgroundColor: accentColor }} />
        </div>
      ) : (
        <AnimatedLoader size={120} />
      )}
      {message && (
        <p
          className="mt-8 max-w-md text-center text-lg animate-pulse"
          style={{
            color: foregroundColor || 'white',
            textShadow: mutedForegroundColor ? `0 0 24px ${mutedForegroundColor}` : undefined,
          }}
        >
          {message}
        </p>
      )}
    </div>
  );
};
